import type { LabelInValue } from 'models';
import { useTranslation } from 'react-i18next';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { Form, FormActions, Input, Modal, MultiValueInput } from 'ui';

export type SmsInviteFormValues = {
  communityId: string;
  phones: string[];
};

type P = {
  community: LabelInValue;
  isSubmitting: boolean;
  onCancel: () => void;
  onSubmit: (data: SmsInviteFormValues) => void;
};

const SmsInviteForm = ({ community, isSubmitting, onCancel, onSubmit }: P) => {
  const { t } = useTranslation();

  return (
    <Form<SmsInviteFormValues> initialValues={{ communityId: community.value }} onFinish={onSubmit}>
      <Form.Item<SmsInviteFormValues> hidden name="communityId" rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item<SmsInviteFormValues>
        extra="Type phone numbers and press enter, space or comma - Or paste from a spreadsheet!"
        label="Phone numbers"
        name="phones"
        rules={[
          {
            // eslint-disable-next-line @typescript-eslint/require-await -- Antd interface need async validator
            async validator(_, value: unknown) {
              if (!Array.isArray(value) || !value.every((phone): phone is string => typeof phone === 'string')) {
                return;
              }

              const invalidPhones = value.filter((phone) => !isValidPhoneNumber(phone));
              if (invalidPhones.length > 0) {
                throw new Error(
                  t(
                    'Error: Some phone numbers are invalid. The number must start with area code + and must not contain any spaces. Example: +12133734253',
                  ),
                );
              }
            },
          },
          {
            required: true,
            type: 'array',
          },
        ]}
      >
        <MultiValueInput allowInvalidValues validateValue={(phoneNumber) => isValidPhoneNumber(phoneNumber)} />
      </Form.Item>
      <Modal.HugContent.Footer>
        <FormActions
          right={{
            children: 'Back',
            onClick() {
              onCancel();
            },
          }}
          submit={{
            children: 'Send invitations',
            loading: isSubmitting,
          }}
        />
      </Modal.HugContent.Footer>
    </Form>
  );
};

export default SmsInviteForm;
